import { Box, Divider, Stack } from '@mui/material'
import { C1CopyTextButton } from 'components/core/copytext/C1CopyTextButton'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1FullPageLoadingSpinner } from 'components/core/page-loader/C1FullPageLoadingSpinner'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { TaskContent } from 'components/pages/task/details/content/TaskContent'
import { TaskValue } from 'lib/actions/task'
import { DisplayModeContext } from 'lib/context/app'
import { Link } from 'lucide-react'
import { TaskServiceGetRequest } from 'pbts/c1/api/task/v1/task'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getTaskApi } from 'redux_toolkit/tasks'

type TaskDrawerProps = {
    taskId: string
    hasPendingActions?: boolean
} & ClosableProps

export const TaskDrawer: FC<TaskDrawerProps> = ({ taskId, hasPendingActions = false }) => {
    const {
        data: task,
        isLoading,
        error,
    } = getTaskApi.useGet(TaskServiceGetRequest.create({ id: taskId, expandMask: { paths: ['*'] } }), { notify: true })

    if (isLoading) {
        return <C1FullPageLoadingSpinner />
    }

    if (!task) {
        return <C1DrawerInner>
            <C1DrawerInner.Content>
                <FormattedMessage id="components_task_dialog_task_not_found" defaultMessage="Task not found." />
            </C1DrawerInner.Content>
        </C1DrawerInner>
    }

    return (
        <C1DrawerInner>
            <C1DrawerInner.Content>
                <APIError
                    title={<FormattedMessage id="components_task_dialog_error_performing_task" defaultMessage="Error loading task" />}
                    err={error}
                    sx={{ mb: 2 }}
                />
                <TaskContent task={task} hasPendingActions={hasPendingActions} />
            </C1DrawerInner.Content>
        </C1DrawerInner>
    )
}

type TaskDrawerHeaderProps = {
    task: TaskValue
    hasPendingActions?: boolean
}

export const TaskDrawerHeader: FC<TaskDrawerHeaderProps> = ({ task, hasPendingActions = false }) => {
    const displayContext = useContext(DisplayModeContext)
    const isInModal = displayContext === 'dialog' || displayContext === 'drawer'
    const intl = useIntl()

    return (
        <>
            <Stack direction="row" gap={2} alignItems="center">
                <Box sx={{ mr: 1 }}>
                    <TaskViewLinkV2 task={task} readOnly={!isInModal} hasPendingActions={hasPendingActions} />
                </Box>
                <Divider flexItem orientation="vertical" sx={{ my: 1 }} />
                <C1CopyTextButton
                    copyValue={`${window.location.origin}/task/${task.numericId}`}
                    Icon={Link}
                    tooltipText={intl.formatMessage({
                        id: 'components_task_drawer_header_copy_link_tooltip',
                        defaultMessage: 'Copy permalink to task',
                    })}
                />
            </Stack>

            <Divider sx={{ my: 4 }} />
        </>
    )
}
